import { useEffect, useState, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import programsIcon from "@/assets/program_iamerica.webp";

// Lazy-load the heavy hero video player so the landing shell paints
// immediately; HLS.js, Cart, dialogs, etc. stream in after first paint.
const HeroFreeVideosSlide = lazy(() =>
  import("@/components/HeroFreeVideosSlide").then((m) => ({ default: m.HeroFreeVideosSlide })),
);

const HeroPlayerSkeleton = () => (
  <div className="max-w-6xl 2xl:max-w-7xl mx-auto w-full md:px-0">
    <div className="aspect-video w-full rounded-xl bg-black/40 border border-white/10 animate-pulse" />
  </div>
);



const SLIDE_MS = 10000;
const FADE_MS = 600;
const SLIDE_COUNT = 5;

const Hero = () => {
  const { t } = useTranslation("landing");
  const [slide, setSlide] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(mq.matches);
    const onChange = () => setReducedMotion(mq.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  useEffect(() => {
    const t = window.setInterval(() => setSlide((s) => (s + 1) % SLIDE_COUNT), SLIDE_MS);
    return () => window.clearInterval(t);
  }, []);

  const fadeClass = (i: number) =>
    `absolute inset-0 flex items-center justify-center gap-2 md:gap-3 min-w-0 transition-opacity ${
      reducedMotion ? "duration-0" : ""
    } ${i === slide ? "opacity-100" : "opacity-0 pointer-events-none"}`;

  const textCls =
    "text-base sm:text-2xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-white whitespace-nowrap";
  const logoCls = "h-7 sm:h-10 md:h-12 lg:h-14 w-auto shrink-0";
  const whiteFilter = { filter: "brightness(0) invert(1)" } as const;
  const accentEm = "font-accent font-medium not-italic";
  const accentStyle = { fontStyle: "italic", fontWeight: 500 } as const;

  // Split helpers that preserve the italic accent markup across locales.
  // "iAmerica" (brand) is identical in EN/ES, so we split on it directly.
  const welcomeParts = t("hero_slides.welcome").split("iAmerica");
  // Slides that italicize the segment after the em-dash separator " — ".
  const bilingualParts = t("hero_slides.bilingual").split(" — ");
  // Signup italicizes the segment before the em-dash separator.
  const signupParts = t("hero_slides.signup").split(" — ");
  // Progress italicizes the final clause after the last ". " separator.
  const progressText = t("hero_slides.progress");
  const progressSplit = progressText.lastIndexOf(". ");
  const progressBefore = progressSplit >= 0 ? progressText.slice(0, progressSplit) : "";
  const progressLast = progressSplit >= 0 ? progressText.slice(progressSplit + 2) : progressText;
  // Groups italicizes the final word (after the last space).
  const groupsText = t("hero_slides.groups");
  const groupsSplit = groupsText.lastIndexOf(" ");
  const groupsBefore = groupsSplit >= 0 ? groupsText.slice(0, groupsSplit) : "";
  const groupsLast = groupsSplit >= 0 ? groupsText.slice(groupsSplit + 1) : groupsText;

  return (
    <section
      id="hero"
      className="relative w-full bg-[#070e1e] pt-[75px] pb-2 md:pt-[105px] md:pb-16 overflow-hidden"
    >
      
      <video
        autoPlay
        loop
        muted
        playsInline
        onLoadedData={(e) => e.currentTarget.classList.add("opacity-100")}
        className="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-[1500ms] ease-out"
        src={GRID1_VIDEO_URL}
      />
      <div className="absolute inset-0 bg-[#223a60]/70" />
      
      <div className="container mx-auto px-[15px] md:px-4 lg:px-6 relative z-10">
        <div className="grid min-w-0 w-full grid-cols-[minmax(0,1fr)]">
          <div style={{ gridArea: "1 / 1" }} className="min-w-0 w-full">
            
            <h1 className="sr-only">{t("hero.sr_title")}</h1>

            {/* Rotating header — fixed height row keeps layout stable */}
            <div
              aria-live="polite"
              aria-hidden="true"
              className="relative mb-4 md:mb-6 px-[10px] md:px-2 h-7 sm:h-10 md:h-12 lg:h-14"
              style={{ transitionDuration: `${FADE_MS}ms` }}
            >
              
              {/* Slide 0 — Welcome */}
              <div className={fadeClass(0)} style={{ transitionDuration: `${FADE_MS}ms` }}>
                <span className={textCls}>
                  {welcomeParts[0]}
                  <em className={accentEm} style={accentStyle}>iAmerica</em>
                  {welcomeParts[1]}
                </span>
                <img
                  src="/iamerica-logo.svg"
                  alt={t("hero.logo_alt")}
                  className={logoCls}
                  style={whiteFilter}
                />
              </div>

              {/* Slide 1 — Progress / certificate */}
              <div className={fadeClass(1)} style={{ transitionDuration: `${FADE_MS}ms` }}>
                <span className={textCls}>
                  {progressBefore}.{" "}
                  <em className={accentEm} style={accentStyle}>{progressLast}</em>
                </span>
                <img
                  src={programsIcon}
                  alt=""
                  aria-hidden="true"
                  className={logoCls}
                  style={whiteFilter}
                />
              </div>

              {/* Slide 2 — Sign up */}
              <div className={fadeClass(2)} style={{ transitionDuration: `${FADE_MS}ms` }}>
                <span className={textCls}>
                  <em className={accentEm} style={accentStyle}>{signupParts[0]}</em>
                  {" — "}{signupParts[1]}
                </span>
                <Link
                  to="/auth?tab=signup"
                  aria-label={t("hero_slides.signup")}
                  className={`${logoCls} inline-flex items-center justify-center rounded-full bg-[#fcb424] text-[#070e1e] font-extrabold px-4 sm:px-5 md:px-6 hover:brightness-110 transition`}
                >
                  <span className="text-xs sm:text-sm md:text-base whitespace-nowrap">
                    {t("nav.get_all_access")}
                  </span>
                </Link>
              </div>

              {/* Slide 3 — Bilingual */}
              <div className={fadeClass(3)} style={{ transitionDuration: `${FADE_MS}ms` }}>
                <span className={textCls}>
                  {bilingualParts[0]}
                  {" — "}
                  <em className={accentEm} style={accentStyle}>{bilingualParts[1]}</em>
                </span>
                <img
                  src="/iamerica-logo.svg"
                  alt=""
                  aria-hidden="true"
                  className={logoCls}
                  style={whiteFilter}
                />
              </div>

              {/* Slide 4 — Groups */}
              <div className={fadeClass(4)} style={{ transitionDuration: `${FADE_MS}ms` }}>
                <span className={textCls}>
                  {groupsBefore}{" "}
                  <em className={accentEm} style={accentStyle}>{groupsLast}</em>
                </span>
                <span
                  aria-hidden="true"
                  className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl leading-none shrink-0"
                  style={whiteFilter}
                >
                  👥
                </span>
              </div>
            </div>

            <Suspense fallback={<HeroPlayerSkeleton />}>
              <HeroFreeVideosSlide />
            </Suspense>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
import { GRID1_VIDEO_URL } from "@/lib/grid1Asset";
